import viagsApiDev from "../../chats/api/axiosClientdev";
import { LinkReviewBody } from "../../chats/types";
import { DataRecord, GetChatsPayload, JoinAChatRoomPayload, PinMessageRequest } from "../types";
import {
  AddMemberInRoomBody,
  ChatItem,
  CheckRoomAdminBody,
  CreateChatRoomBody,
  GetResourceInRoomBody,
  GetResourceInRoomResponse,
  RemoveMemberInRoomBody,
  RoomInfo,
  SetRoomAdminBody,
  UpdateChatRoomBody,
  ChatMessage,
  AddMessageReactionBody,
  GetReactionsInRoomBody,
  GetMessageReactionBody,
  MessageReaction,
  GetMessageReaction,
  MessageMetadata,
  ApiError,
  ChatThreadClient,
  MessageIterator,
  SearchMessage,
  ParamsSearchMessage,
  PinRoomBody,
  TransferOwnerBody,
  MessageConfirmationBody,
  GetMemberConfirmationPayload,
  MemberConfirmation,
  GetReaderItem,
  renderSchema,
} from "../types/chat";

export const chatApi = {
  getChats: async (body: GetChatsPayload) => {
    const response = await viagsApiDev.post<DataRecord<ChatItem[]>>("/api/chat/get-chats", body);
    return response.data.Data;
  },

  /**
   * Tạo Azure Communication Services client từ thông tin phòng
   * @param roomInfo - Thông tin phòng chat
   * @returns Object chứa chatClient và chatThreadClient
   */
  createClient: async (roomInfo: RoomInfo) => {
    const { Token, ThreadId, Endpoint } = roomInfo || {};

    if (!Token || !Endpoint || !ThreadId) {
      return null;
    }

    try {
      // Import động để tránh lỗi khi chưa cài đặt các package Azure
      const { AzureCommunicationTokenCredential } = await import("@azure/communication-common");
      const { ChatClient } = await import("@azure/communication-chat");

      const tokenCredential = new AzureCommunicationTokenCredential(Token);
      const chatClient = new ChatClient(Endpoint, tokenCredential);
      const chatThreadClient = chatClient.getChatThreadClient(ThreadId);

      // Bắt đầu realtime notifications
      await chatClient.startRealtimeNotifications();

      return { chatClient, chatThreadClient };
    } catch (error) {
      console.error("Lỗi khi tạo Azure Communication Services client:", error);
      return null;
    }
  },

  createChatRoom: async (body: CreateChatRoomBody) => {
    const response = await viagsApiDev.post<DataRecord<RoomInfo>>("/api/chat/create-a-chat-room", body);
    return response.data;
  },
  joinAChatRoom: async (body: JoinAChatRoomPayload) => {
    const response = await viagsApiDev.post<DataRecord<RoomInfo>>("/api/chat/join-a-chat-room", body);

    return response.data;
  },
  addMemberInRoom: async (body: AddMemberInRoomBody) => {
    const response = await viagsApiDev.post<DataRecord<boolean>>("/api/chat/add-participants", body);

    return response.data;
  },
  removeMemberInRoom: async (body: RemoveMemberInRoomBody) => {
    const response = await viagsApiDev.post<DataRecord<boolean>>("/api/chat/remove-participants", body);
    return response.data;
  },

  // API tin nhắn được ghim
  getPinnedMessages: async (params: { roomId: string; keyword?: string; pageIndex?: number; pageSize?: number }) => {
    const response = await viagsApiDev.get("/api/chat/get-pin-message", { params });
    return response.data;
  },

  getPinnedMessageIds: async (roomId: string) => {
    const response = await viagsApiDev.get("/api/chat/get-message-id-pin-message", {
      params: { roomId },
    });
    return response.data;
  },
  setRoomAdmin: async (body: SetRoomAdminBody) => {
    const response = await viagsApiDev.post<DataRecord<boolean>>("/api/chat/set-room-admin", body);

    return response.data;
  },
  checkListUserInRoomAdmin: async (body: string[], roomId: string) => {
    const response = await viagsApiDev.post<DataRecord<string[]>>(
      `/api/chat/check-list-user-in-room-admin?roomId=${roomId}`,
      body,
    );
    return response.data;
  },
  checkRoomAdmin: async (body: CheckRoomAdminBody) => {
    console.log("🔍 checkRoomAdmin API called with:", body);
    const response = await viagsApiDev.post<DataRecord<boolean>>("/api/chat/check-room-admin", body);
    console.log("🔍 checkRoomAdmin API response:", response.data);
    return response.data;
  },
  getResourceInRoom: async (body: GetResourceInRoomBody) => {
    const response = await viagsApiDev.get<DataRecord<GetResourceInRoomResponse>>("/api/chat/get-resource-in-room", {
      params: body,
    });

    return response.data;
  },

  /**
   * Gửi tin nhắn đến phòng chat
   * @param roomId - ID phòng chat
   * @param content - Nội dung tin nhắn
   * @param metadata - Metadata tùy chọn (đính kèm, trả lời, xem trước link)
   * @returns Phản hồi từ API
   */
  sendMessage: async (roomId: string, content: string, metadata?: MessageMetadata) => {
    try {
      const response = await viagsApiDev.post("/api/chat/send-message", {
        RoomId: roomId,
        Content: content,
        MetaData: metadata,
      });

      return response.data;
    } catch (error) {
      const apiError = error as ApiError;
      console.error("Lỗi gửi tin nhắn:", apiError.response?.data || apiError.message);
      throw error;
    }
  },

  /**
   * Lấy dữ liệu xem trước link từ URL
   * @param url - URL cần lấy preview
   * @returns Dữ liệu xem trước link
   */
  getLinkPreview: async (url: string) => {
    const response = await viagsApiDev.get<DataRecord<LinkReviewBody>>(`/api/linkpreview/get-link-preview?url=${url}`);

    return response.data;
  },

  /**
   * Cập nhật tin nhắn hiện có
   * @param roomId - ID phòng chat
   * @param messageId - ID tin nhắn cần cập nhật
   * @param content - Nội dung mới
   * @param metadata - Metadata tùy chọn
   * @returns Phản hồi từ API
   */
  updateMessage: async (roomId: string, messageId: string, content: string, metadata?: MessageMetadata) => {
    try {
      const response = await viagsApiDev.post("/api/chat/update-message", {
        RoomId: roomId,
        MessageId: messageId,
        Content: content,
        MetaData: metadata,
      });

      return response.data;
    } catch (error) {
      const apiError = error as ApiError;
      console.error("Lỗi cập nhật tin nhắn:", apiError.response?.data || apiError.message);
      throw error;
    }
  },

  /**
   * Xóa tin nhắn
   * @param roomId - ID phòng chat
   * @param messageId - ID tin nhắn cần xóa
   */
  deleteMessage: async (roomId: string, messageId: string) => {
    try {
      const response = await viagsApiDev.post("/api/chat/delete-message", {
        RoomId: roomId,
        MessageId: messageId,
      });

      return response.data;
    } catch (error) {
      const apiError = error as ApiError;
      console.error("Lỗi xóa tin nhắn:", apiError.response?.data || apiError.message);
      throw error;
    }
  },

  /**
   * Ghim hoặc bỏ ghim tin nhắn
   * @param body - Thông tin request chứa roomId, messageId và trạng thái pin
   * @returns Phản hồi từ API
   */
  pinMessage: async (body: PinMessageRequest) => {
    const response = await viagsApiDev.post<DataRecord<boolean>>("/api/chat/pin-message", body);
    return response.data;
  },

  /**
   * Thêm hoặc xóa reaction cho tin nhắn
   * @param body - { MessageId, roomId, Reaction }
   * @returns Phản hồi từ API
   */
  addMessageReaction: async (body: AddMessageReactionBody) => {
    try {
      const response = await viagsApiDev.post("/api/chat/add-message-reaction", body);
      return response.data;
    } catch (error) {
      const apiError = error as ApiError;
      console.error("Lỗi thêm reaction:", apiError.response?.data || apiError.message);
      throw error;
    }
  },

  /**
   * Lấy tất cả reaction trong phòng
   * @param body - { roomId, pageIndex, pageSize }
   * @returns Mảng các MessageReaction
   */
  getReactionsInRoom: async (body: GetReactionsInRoomBody) => {
    try {
      const response = await viagsApiDev.post<DataRecord<MessageReaction[]>>("/api/chat/get-reaction-in-room", body);
      return response.data;
    } catch (error) {
      const apiError = error as ApiError;
      console.error("Lỗi lấy reactions trong phòng:", apiError.response?.data || apiError.message);
      throw error;
    }
  },

  /**
   * Lấy chi tiết reaction cho một tin nhắn cụ thể
   * @param body - { messageId, pageIndex, pageSize }
   * @returns Mảng GetMessageReaction với thông tin người dùng
   */
  getMessageReactions: async (body: GetMessageReactionBody) => {
    try {
      const response = await viagsApiDev.post<DataRecord<GetMessageReaction[]>>(
        "/api/chat/get-message-reactions",
        body,
      );
      return response.data;
    } catch (error) {
      const apiError = error as ApiError;
      console.error("Lỗi lấy chi tiết reactions:", apiError.response?.data || apiError.message);
      throw error;
    }
  },

  /**
   * Lấy tin nhắn với phân trang sử dụng Azure ACS iterator
   * QUAN TRỌNG: Trả về tin nhắn theo thứ tự MỚI NHẤT TRƯỚC trong mỗi trang
   * Đây là thứ tự tự nhiên từ Azure API
   */
  getMessages: async (chatThreadClient: ChatThreadClient | null, iterator?: MessageIterator, maxPageSize = 50) => {
    if (!chatThreadClient) {
      return {
        messages: [],
        iterator: undefined,
      };
    }

    try {
      const pagingIterator = iterator ?? chatThreadClient.listMessages({ maxPageSize }).byPage({ maxPageSize });
      const result = await pagingIterator.next();

      if (result.done) {
        return {
          messages: [],
          iterator: undefined,
        };
      }

      // Azure trả về tin nhắn theo thứ tự mới nhất trước
      // Giữ nguyên thứ tự này - không đảo ngược ở đây
      // Chuyển đổi ChatMessage từ Azure SDK sang kiểu AppChatMessage của chúng ta
      const messages = Array.from(result.value as unknown as ChatMessage[]);

      return {
        messages,
        iterator: result.done ? undefined : pagingIterator,
      };
    } catch (error) {
      console.error("❌ Error in getMessages:", error);
      return {
        messages: [],
        iterator: undefined,
      };
    }
  },

  /**
   * Tìm kiếm tin nhắn trong phòng
   * @param params - { roomId, keyword, pageIndex, pageSize }
   * @returns Mảng các SearchMessage
   */
  searchMessageInRoom: async (params: ParamsSearchMessage) => {
    if (!params.keyword) return { Data: [] };
    const response = await viagsApiDev.get<DataRecord<SearchMessage[]>>("/api/chat/get-message-in-room", {
      params,
    });
    return response.data;
  },

  /**
   * Cập nhật thông tin phòng chat (tên, avatar, loại)
   * @param body - { roomId, newName?, avatarUrl?, roomType? }
   * @returns Phản hồi từ API
   */
  updateChatRoom: async (body: UpdateChatRoomBody) => {
    const response = await viagsApiDev.post<DataRecord<boolean>>("/api/chat/rename", body);
    return response.data;
  },

  /**
   * Upload avatar lên server
   * @param file - File ảnh cần upload
   * @returns URL của ảnh đã upload
   */
  uploadAvatar: async (file: File) => {
    const formData = new FormData();
    formData.append("files", file);

    const response = await viagsApiDev.post<DataRecord<string[]>>("/api/file/upload-multiple-file", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data.Data[0]; // Trả về URL đầu tiên
  },

  /**
   * Pin hoặc bỏ pin phòng chat
   * @param body - { roomId, pin }
   * @returns Phản hồi từ API
   */
  pinRoom: async (body: PinRoomBody) => {
    const response = await viagsApiDev.post<DataRecord<boolean>>("/api/chat/pin-room", body);
    return response.data;
  },
  muteRoom: async (roomId: string) => {
    const response = await viagsApiDev.post<DataRecord<boolean>>("/api/chatroom/muteroom", JSON.stringify(roomId));
    return response.data;
  },
  closeRoom: async (roomId: string) => {
    const response = await viagsApiDev.post<DataRecord<boolean>>("/api/chatroom/closeroom", JSON.stringify(roomId));

    return response;
  },
  transferOwner: async (body: TransferOwnerBody) => {
    const response = await viagsApiDev.post<DataRecord<boolean>>("/api/chatroom/transferownership", body);
    return response.data;
  },
  confirmMessage: async (body: MessageConfirmationBody) => {
    const response = await viagsApiDev.post<DataRecord<boolean>>("/api/chat/confirm-message", body);
    return response.data;
  },
  getMemberConfirmation: async (body: GetMemberConfirmationPayload) => {
    const response = await viagsApiDev.post<DataRecord<MemberConfirmation[]>>(
      "/api/chat/get-member-confirmation",
      body,
    );
    return response.data;
  },

  /**
   * Xuất báo cáo xác nhận thành viên
   * @param messageId - ID của tin nhắn
   * @returns Blob chứa nội dung file báo cáo
   */
  exportMemberConfirmation: async (messageId: string, roomId: string) => {
    const response = await viagsApiDev.post(
      "/api/chat/export-member-confirmation",
      { messageId, roomId },
      { responseType: "blob" },
    );
    return response.data;
  },

  /**
   * Lấy danh sách người xem tin nhắn
   * @param body - { roomId, seen?, read? }
   * @returns Mảng các GetReaderItem
   */
  getReader: async (body: renderSchema) => {
    const response = await viagsApiDev.post<DataRecord<GetReaderItem[]>>("/api/chat/get-reader", body);
    return response.data;
  },
  getConfirmedMessageIds: async (roomId: string) => {
    try {
      const response = await viagsApiDev.post<DataRecord<string[]>>("/api/chat/get-message-confirmation", {
        roomId,
        pageIndex: 1,
        pageSize: 1000,
      });
      return response.data;
    } catch (error) {
      console.log("Error fetching confirmed messages:", error);
      return {
        StatusCode: 500,
        Message: "Error",
        TotalRecord: 0,
        Data: [],
      };
    }
  },
};
